/* Temporary this functionality may not work and plays the role of mock,
 will be improved and implemented in the next task */

import type { CommandHandler } from '../types';
import type { ColumnMetadata } from '../../types';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function getImportColumns(columns: ColumnMetadata[]) {
  return columns.filter(
    (col) =>
      col.visible !== false &&
      col.field &&
      col.field !== 'actions' &&
      !col.field.startsWith('_') &&
      col.field !== 'Id'
  );
}

function columnHeaderLabel(col: ColumnMetadata): string {
  return (col.title || col.caption || col.field || '').trim();
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase();
}

const BOOLEAN_TRUE = new Set(['yes', 'true', '1', 'y']);
const BOOLEAN_FALSE = new Set(['no', 'false', '0', 'n']);

function resolveImportedValue(
  raw: unknown,
  col: ColumnMetadata,
  lookupMaps?: Map<string, Record<string, string>>
): unknown {
  if (raw === null || raw === undefined || raw === '') {
    return col.defaultValue ?? '';
  }

  const fieldName = col.dataField || col.field;
  const lookupMap = fieldName ? lookupMaps?.get(fieldName) : undefined;
  if (lookupMap) {
    const display = String(raw).trim();
    const entry = Object.entries(lookupMap).find(([, label]) => label === display);
    if (entry) {
      return entry[0];
    }
  }

  if (col.type === 'boolean' || col.dataType === 'boolean') {
    if (typeof raw === 'boolean') return raw;
    const lower = String(raw).trim().toLowerCase();
    if (BOOLEAN_TRUE.has(lower)) return true;
    if (BOOLEAN_FALSE.has(lower)) return false;
  }

  return raw;
}

/**
 * Handler for 'import' command — reads an Excel file and adds rows (legacy GridActions.Import).
 */
export const importCommandHandler: CommandHandler = async (_command, context) => {
  const { columns, lookupMaps, onAddRowWithData, inlineEditable, metadata } = context;

  if (!inlineEditable && metadata.gridType !== 'InlineEdit') {
    return {
      success: false,
      error: 'Import is only available on inline-editable grids',
    };
  }

  if (!onAddRowWithData) {
    return {
      success: false,
      error: 'Import is not available',
    };
  }

  const importColumns = getImportColumns(columns);
  if (importColumns.length === 0) {
    return {
      success: false,
      error: 'No importable columns configured',
    };
  }

  const file = await pickExcelFile();
  if (!file) {
    return { success: false };
  }

  try {
    const { default: ExcelJS } = await import('exceljs');
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const importedRows: Record<string, unknown>[] = [];

    for (const worksheet of workbook.worksheets) {
      if (!worksheet.rowCount || worksheet.rowCount < 2) continue;

      const headerRow = worksheet.getRow(1);
      const headersByCol = new Map<number, string>();
      headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const header = String(cell.text ?? '').trim();
        if (header) headersByCol.set(colNumber, header);
      });

      const columnMap: { col: ColumnMetadata; sheetCol: number }[] = [];
      for (const [sheetCol, header] of headersByCol) {
        const col = importColumns.find(
          (c) => normalizeHeader(columnHeaderLabel(c)) === normalizeHeader(header)
        );
        if (col) columnMap.push({ col, sheetCol });
      }

      if (columnMap.length === 0) continue;

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;

        const rowData: Record<string, unknown> = {};
        let hasValue = false;

        for (const { col, sheetCol } of columnMap) {
          const field = col.field || col.dataField;
          if (!field) continue;
          const cell = row.getCell(sheetCol);
          const raw = cell.value;
          const value =
            raw !== null && raw !== undefined && raw !== ''
              ? resolveImportedValue(raw, col, lookupMaps)
              : undefined;
          if (value !== undefined && value !== '') {
            rowData[field] = value;
            hasValue = true;
          }
        }

        if (hasValue) {
          importedRows.push(rowData);
        }
      });
    }

    if (importedRows.length === 0) {
      return {
        success: false,
        error: 'No rows found in the file. Check that column headers match the grid.',
      };
    }

    onAddRowWithData(importedRows);

    return {
      success: true,
      message: `Imported ${importedRows.length} row${importedRows.length > 1 ? 's' : ''}`,
    };
  } catch (error: unknown) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Import failed',
    };
  }
};

function pickExcelFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = XLSX_MIME;
    input.onchange = () => {
      resolve(input.files?.[0] ?? null);
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}
